/**
 * @internal — sealed form-instance bridge.
 *
 * Maps a public {@link FmFormHandle} to its underlying AntD `FormInstance` in a
 * module-private WeakMap. Only the DS form layer (`useFmForm`, `FmForm`,
 * `FmFormShell`) resolves the raw instance; route/page code holds the sealed
 * handle and can never reach the instance through its type. Do NOT re-export
 * these from the public `@/components/ui` barrel.
 */
import type { FormInstance } from 'antd';

const instanceBridge = new WeakMap<object, FormInstance>();

/** Bind a freshly-created sealed handle to its backing AntD form instance. */
export function bindFmFormInstance(handle: object, instance: FormInstance): void {
  instanceBridge.set(handle, instance);
}

/**
 * Resolve the backing AntD form instance for a sealed handle. DS-internal only.
 * Throws if the handle was not produced by `useFmForm()` (unbound).
 */
export function resolveFmFormInstance(handle: object): FormInstance {
  const instance = instanceBridge.get(handle);
  if (!instance) {
    throw new Error(
      'FmFormHandle is not bound to a form instance. Create the handle with useFmForm().',
    );
  }
  return instance;
}
